import { useState } from "react";

export interface ISerialTreeNode {
  id: string;
  level: number;
  serial_number: string;
  status: "in_stock" | "sold" | "recalled" | "damaged";
  children_count: number;
  opened: boolean;
  contents: ISerialTreeNode[];
}

interface ISerialTreeNodeProps {
  node: ISerialTreeNode;
  depth: number;
}

// Left-border accent per nesting depth, cycling if deeper than 4 levels —
// mirrors add-inventory/subs/InventorySerialSection.tsx's DEPTH_ACCENTS idiom.
const DEPTH_ACCENTS = [
  "border-l-blue-400",
  "border-l-purple-400",
  "border-l-amber-400",
  "border-l-teal-400",
];

const STATUS_LABELS: Record<ISerialTreeNode["status"], string> = {
  in_stock: "Available",
  sold: "Sold",
  recalled: "Recalled",
  damaged: "Damaged",
};

const STATUS_STYLES: Record<ISerialTreeNode["status"], string> = {
  in_stock: "bg-green-100 text-green-700",
  sold: "bg-gray-100 text-gray-600",
  recalled: "bg-red-100 text-red-700",
  damaged: "bg-orange-100 text-orange-700",
};

function ChevronIcon({ rotated }: { rotated: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className={`h-3 w-3 flex-shrink-0 text-gray-400 transition-transform ${rotated ? "rotate-90" : ""}`}
    >
      <path
        fillRule="evenodd"
        d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
        clipRule="evenodd"
      />
    </svg>
  );
}

// Row chrome steps down in density with depth. Every row is left-aligned
// and fills the width of whatever level it's at — depth is communicated
// entirely by the children block below indenting to the right when opened,
// not by anything on the row itself.
const ROW_STYLES_BY_DEPTH = [
  {
    row: "rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 hover:bg-gray-100",
    label: "text-sm font-semibold text-gray-900",
    badge: "text-xs",
  },
  {
    row: "rounded-md border border-gray-200 bg-white px-3 py-2.5 hover:bg-gray-50",
    label: "text-sm font-medium text-gray-800",
    badge: "text-xs",
  },
  {
    row: "rounded-md border border-gray-100 bg-white px-2.5 py-2 hover:bg-gray-50",
    label: "text-[0.8125rem] font-medium text-gray-700",
    badge: "text-[0.6875rem]",
  },
];
const MAX_STYLE_DEPTH = ROW_STYLES_BY_DEPTH.length - 1;

// Read-only recursive node in a batch's serial tree (MED-652 stage 3).
// A container (children_count > 0) always shows its child count and an
// expand arrow, even when that count is 0 — containers never disappear.
// Each node's expand/collapse state is independent, so multiple branches
// can be open at once. Leaf/base-unit nodes show a status badge instead.
export function SerialTreeNode({ node, depth }: ISerialTreeNodeProps) {
  const [collapsed, setCollapsed] = useState(true);
  const isContainer = node.children_count > 0;
  const depthAccent = DEPTH_ACCENTS[depth % DEPTH_ACCENTS.length];
  const { row, label, badge } =
    ROW_STYLES_BY_DEPTH[Math.min(depth, MAX_STYLE_DEPTH)];

  return (
    <div className="w-full">
      {/* Row: everything left-aligned in document order — chevron, then
          serial number, then badges — with justify-start pinned explicitly
          so nothing can end up pushed to the right edge. */}
      <div
        className={`flex w-full items-center justify-start gap-2 text-left transition-colors ${row} ${isContainer ? "cursor-pointer" : ""}`}
        onClick={() => isContainer && setCollapsed((c) => !c)}
      >
        {isContainer ? (
          <ChevronIcon rotated={!collapsed} />
        ) : (
          <span className="inline-block h-3 w-3 flex-shrink-0" />
        )}
        <span className={`break-all ${label}`}>{node.serial_number}</span>

        {isContainer ? (
          <span className={`whitespace-nowrap rounded bg-gray-100 px-1.5 py-0.5 font-medium text-gray-600 ${badge}`}>
            {node.children_count} item{node.children_count === 1 ? "" : "s"}
          </span>
        ) : (
          <span className={`whitespace-nowrap rounded px-1.5 py-0.5 font-medium ${STATUS_STYLES[node.status]} ${badge}`}>
            {STATUS_LABELS[node.status]}
          </span>
        )}

        {node.opened && (
          <span className={`whitespace-nowrap rounded bg-yellow-100 px-1.5 py-0.5 font-medium text-yellow-800 ${badge}`}>
            OPENED
          </span>
        )}
      </div>

      {/* Children: this block is what "opens to the right" — it only
          exists once expanded, and is indented as a whole (border + margin
          + padding) rather than each row indenting itself individually. */}
      {isContainer && !collapsed && (
        <div
          className={`mt-1 flex w-full flex-col gap-1 border-l-2 ${depthAccent}`}
          style={{ marginLeft: "0.9rem", paddingLeft: "0.75rem" }}
        >
          {node.contents.map((child) => (
            <SerialTreeNode key={child.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}
